import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Tag } from "lucide-react";
import SEOHead from "../components/SEOHead";
import ProductCard from "../components/ProductCard";
import StoreSwitcher from "../components/StoreSwitcher";
import { getSpecialDetail, getSpecials } from "../lib/api";
import { classifySpecial } from "../lib/promos";

export default function SpecialDetail() {
    const { storeSlug, specialId } = useParams();
    const [data, setData] = useState(null);
    const [allSpecials, setAllSpecials] = useState([]);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const applyData = (d) => {
            if (!cancelled) {
                setData(d);
                setErr(null);
            }
        };
        const applyErr = () => {
            if (!cancelled) setErr("error");
        };
        const applySpecials = (d) => {
            if (!cancelled) setAllSpecials(d?.specials || []);
        };
        getSpecialDetail(storeSlug, specialId).then(applyData).catch(applyErr);
        getSpecials(storeSlug).then(applySpecials).catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [storeSlug, specialId]);

    // Show loader whenever the current cached `data` doesn't match the URL —
    // derived, so the linter doesn't trip on sync setState inside the effect.
    const dataMatchesUrl = data && data.special && data.special.id === specialId;

    const specialsById = useMemo(() => {
        const idx = {};
        allSpecials.forEach((s) => {
            const c = classifySpecial(s);
            if (c) idx[c.id] = c;
        });
        return idx;
    }, [allSpecials]);

    if (err && !dataMatchesUrl) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center">
                <h1 className="font-serif text-4xl font-light mb-6 text-zen-ink">
                    Special not available.
                </h1>
                <Link to={`/menu/${storeSlug}`} className="text-zen-olive underline">
                    Back to menu
                </Link>
            </div>
        );
    }
    if (!dataMatchesUrl) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center text-zen-ink-soft">
                Loading…
            </div>
        );
    }

    const { special, products } = data;
    const c = classifySpecial(special);
    const hasHeroImage = !!special?.menuDisplayConfiguration?.image;

    return (
        <>
            <SEOHead
                title={`${special.name} — ZenZest`}
                description={`Shop products on special: ${special.name}. ${products.length} qualifying items at ZenZest.`}
                canonical={`https://weeddispensarynearme.com/menu/${storeSlug}/special/${specialId}`}
            />
            <section className="relative bg-zen-surface border-b border-zen-ink/10">
                <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 py-10 sm:py-14">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <Link
                            to={`/menu/${storeSlug}`}
                            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-zen-ink-soft hover:text-zen-ink"
                        >
                            <ArrowLeft size={12} /> Back to menu
                        </Link>
                        <StoreSwitcher currentSlug={storeSlug} />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-7">
                            <div className="flex items-start gap-3 mb-4">
                                <Tag size={18} className="mt-1 shrink-0 text-zen-olive" />
                                <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive">
                                    {c?.kind === "BOGO"
                                        ? "BOGO"
                                        : c?.kind === "PCT_OFF"
                                          ? "% Off"
                                          : "Promotion"}{" "}
                                    · {products.length} qualifying products
                                </p>
                            </div>
                            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-tight text-zen-ink">
                                {special.name}
                            </h1>
                            {special.menuDisplayConfiguration?.description && (
                                <p className="mt-5 text-sm sm:text-base text-zen-ink-soft max-w-2xl leading-relaxed">
                                    {special.menuDisplayConfiguration.description}
                                </p>
                            )}
                        </div>
                        {hasHeroImage && (
                            <div className="lg:col-span-5">
                                {/* `object-contain` so the entire promo
                                    artwork is visible without crop. Tight
                                    pale-olive frame stands in for the prior
                                    red surround. */}
                                <div className="aspect-[4/3] flex items-center justify-center bg-zen-bg border border-zen-ink/10 p-4">
                                    <img
                                        src={special.menuDisplayConfiguration.image}
                                        alt={special.name}
                                        data-testid="special-hero-image"
                                        className="max-w-full max-h-full object-contain"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <section className="bg-zen-bg max-w-[1400px] mx-auto px-6 sm:px-10 py-14">
                {products.length === 0 ? (
                    <div className="text-center py-12 border border-zen-ink/10 bg-zen-surface">
                        <p className="font-serif text-2xl text-zen-ink font-light mb-3">
                            No qualifying products in stock right now.
                        </p>
                        <Link
                            to={`/menu/${storeSlug}`}
                            className="text-[11px] uppercase tracking-[0.25em] text-zen-olive border-b border-zen-olive/40 pb-1"
                        >
                            Browse full menu
                        </Link>
                    </div>
                ) : (
                    <div
                        data-testid="special-product-grid"
                        className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3"
                    >
                        {products.map((p, i) => (
                            <ProductCard
                                key={`${p.id}-${i}`}
                                product={p}
                                storeSlug={storeSlug}
                                specialsById={specialsById}
                            />
                        ))}
                    </div>
                )}
            </section>
        </>
    );
}
